import React from 'react'
import {
  Admin,
  Resource,
  CustomRoutes,
  localStorageStore,
  StoreContextProvider,
} from 'react-admin'
import { Route } from 'react-router-dom'
import AdminInvitation from './pages/AdminInvitation'

import { Dashboard } from './Dashboard'
import { Login } from './Login'

import { ServiceConfigList, ServiceConfigEdit } from './resources/serviceConfis'
import { NetworkList, NetworkShow, NetworkCreate, NetworkEdit } from './resources/networks'
import { TokenShow, TokenList, TokenCreate, TokenEdit } from './resources/tokens'
import { PartnerShow, PartnerList, PartnerCreate, PartnerEdit } from './resources/partners'
import { CampaignShow, CampaignList, CampaignCreate, CampaignEdit } from './resources/campaigns'
import { UserDepositShow, UserDepositList, UserDepositEdit } from './resources/userDeposits'
import { UserShow, UserList } from './resources/users'
import { ReferralClientList, ReferralClientShow, ReferralClientCreate, ReferralClientEdit } from './resources/referralClients'
import { UserUserReferralList, UserUserReferralShow } from './resources/userReferrals'

// https://mui.com/material-ui/material-icons/?theme=Outlined&query=report
import ServiceConfigIcon from '@mui/icons-material/TuneOutlined'
import NetworkIcon from '@mui/icons-material/Lan'
import TokenIcon from '@mui/icons-material/Token'
import PartnerIcon from '@mui/icons-material/Handshake'
import CampaignIcon from '@mui/icons-material/Campaign'
import UserDepositIcon from '@mui/icons-material/AttachMoney'
import UserIcon from '@mui/icons-material/Group'
import UserReferralIcon from '@mui/icons-material/SendOutlined'
import ReferralClientIcon from '@mui/icons-material/Share'

import { authProvider, buildResourcePermissions } from './authProvider'
import { dataProvider } from './dataProvider'

import MyLayout from './layout/Layout'
import { ProfileEdit } from './pages/profile'
import { customDarkTheme, customLightTheme, logInBackgroundImage } from './theme/myCustomTheme'

const resources = [
  <Resource
    name="serviceConfigs"
    list={ServiceConfigList}
    edit={ServiceConfigEdit}
    recordRepresentation="title"
    icon={ServiceConfigIcon}
    options={{ label: 'Service Config' }}
  />,
  <Resource
    key="networks"
    name="networks"
    list={NetworkList}
    edit={NetworkEdit}
    create={NetworkCreate}
    show={NetworkShow}
    recordRepresentation="name"
    icon={NetworkIcon}
    options={{ label: 'Networks' }}
  />,
  <Resource
    key="tokens"
    name="tokens"
    list={TokenList}
    edit={TokenEdit}
    create={TokenCreate}
    show={TokenShow}
    recordRepresentation="name"
    icon={TokenIcon}
    options={{ label: 'Tokens' }}
  />,
  <Resource
    key="partners"
    name="partners"
    list={PartnerList}
    edit={PartnerEdit}
    create={PartnerCreate}
    show={PartnerShow}
    recordRepresentation="title"
    icon={PartnerIcon}
    options={{ label: 'Partners' }}
  />,
  <Resource
    key="campaigns"
    name="campaigns"
    list={CampaignList}
    edit={CampaignEdit}
    create={CampaignCreate}
    show={CampaignShow}
    recordRepresentation="title"
    icon={CampaignIcon}
    options={{ label: 'Campaigns' }}
  />,
  <Resource
    key="userDeposits"
    name="userDeposits"
    list={UserDepositList}
    edit={UserDepositEdit}
    show={UserDepositShow}
    recordRepresentation="campaignId"
    icon={UserDepositIcon}
    options={{ label: 'UserDeposits' }}
  />,
  <Resource
    key="users"
    name="users"
    list={UserList}
    show={UserShow}
    recordRepresentation="name"
    icon={UserIcon}
    options={{ label: 'Users' }}
  />,
  <Resource
    key="userReferrals"
    name="userReferrals"
    list={UserUserReferralList}
    show={UserUserReferralShow}
    recordRepresentation="invitedBy"
    icon={UserReferralIcon}
    options={{ label: 'Referrals' }}
  />,
  <Resource
    name="referralClients"
    list={ReferralClientList}
    edit={ReferralClientEdit}
    create={ReferralClientCreate}
    show={ReferralClientShow}
    recordRepresentation="type"
    icon={ReferralClientIcon}
    options={{ label: 'ReferralClients' }}
  />,
]

const filterResources = () => {
  const resourcesMap = buildResourcePermissions()
  const resourceNames = Object.keys(resourcesMap)

  return resources
    .filter((r) => resourceNames.includes(r.props.name))
    .map((resource) => {
      const resourceName = resource.props.name
      const permissions = resourcesMap[resourceName]

      const newProps = {
        name: resource.props.name,
        recordRepresentation: resource.props.recordRepresentation,
        icon: resource.props.icon,
        options: resource.props.options,
      }

      if (permissions.isRead) {
        if (resource.props.list) {
          newProps.list =
            typeof resource.props.list === 'function'
              ? resource.props.list({
                  isEdit: permissions.isEdit || false,
                  isDelete: permissions.isDelete || false,
                  isCreate: permissions.isCreate || false,
                })
              : resource.props.list
        } else {
          newProps.list = undefined
        }
        if (resource.props.show) {
          newProps.show = resource.props.show
        } else {
          newProps.show = undefined
        }
      } else {
        newProps.list = undefined
        newProps.show = undefined
      }

      if (permissions.isEdit && resource.props.edit) {
        newProps.edit = resource.props.edit
      } else {
        newProps.edit = undefined
      }

      if (permissions.isCreate && resource.props.create) {
        newProps.create = resource.props.create
      } else {
        newProps.create = undefined
      }

      return React.cloneElement(resource, newProps)
    })
}

const store = localStorageStore(undefined, 'talentum-deals-admin')

const App = () => (
  <Admin
    loginPage={
      <Login
        style={{
          backgroundImage: logInBackgroundImage,
        }}
      />
    }
    dataProvider={dataProvider}
    authProvider={authProvider}
    store={store}
    dashboard={Dashboard}
    layout={MyLayout}
    theme={customLightTheme}
    darkTheme={customDarkTheme}
    defaultTheme="dark"
  >
    <CustomRoutes>
      <Route key="my-profile" path="/my-profile" element={<ProfileEdit />} />
    </CustomRoutes>
    {filterResources}

    <CustomRoutes noLayout disableAuthentication>
      <Route key="admin-invitation" path="/admin-invitation" element={<AdminInvitation />} />
    </CustomRoutes>
  </Admin>
)

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
)

export default AppWrapper
